import { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import i18n from 'i18n'
import { timeFormat } from 'utils'
import refreshSvg from 'assets/images/button-refresh-black-16.svg'
import IFace, { type ReadReceiptStatus } from '../../ViewerIFace'

import styles from './style.module.scss'

interface Props {
  status: ReadReceiptStatus
  onClick: () => void
  onRefresh?: () => void
}

export default function ReadReceiptChip({ status, onClick, onRefresh }: Props) {
  const isAllRead = status.unread === 0 && status.total > 0
  const lastUpdatedLabel = formatLastUpdated(getLastUpdatedAt(status))

  const [isRotating, setIsRotating] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // 언마운트 시 타이머 정리
  useEffect(
    () => () => {
      if (timerRef.current != null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    },
    [],
  )

  const handleRefreshClick = () => {
    if (onRefresh == null) return
    setIsRotating(true)
    if (timerRef.current != null) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      setIsRotating(false)
      timerRef.current = null
    }, 1000)
    onRefresh()
  }

  return (
    <div className={classNames(styles.readReceiptContainer, 'readReceiptContainer')}>
      <button
        type="button"
        className={classNames(styles.chip, isAllRead ? styles.chipAll : styles.chipPartial)}
        onClick={onClick}
      >
        <span className={classNames(styles.dot)} />
        <span className={classNames(styles.label)}>{i18n.t('readReceipt.chip.label')}</span>
        <span className={classNames(styles.count)}>{`${status.read}/${status.total}`}</span>
      </button>
      <span className={classNames(styles.lastUpdated)}>
        {i18n.t('readReceipt.chip.lastUpdated', { time: lastUpdatedLabel })}
      </span>
      {onRefresh != null && !isAllRead && (
        <button
          type="button"
          className={classNames(styles.refresh, { [styles.rotating]: isRotating })}
          onClick={handleRefreshClick}
          aria-label={i18n.t('readReceipt.chip.refresh')}
        >
          <img src={refreshSvg} alt="" width={16} height={16} />
        </button>
      )}
    </div>
  )
}

/** recipients[*].first_read_real_at 중 max 또는 sent_at fallback. */
function getLastUpdatedAt(status: ReadReceiptStatus): string {
  const times = status.recipients
    .map((r) => r.first_read_real_at)
    .filter((v): v is string => v != null)
  if (times.length > 0) return times.reduce((a, b) => (a > b ? a : b))
  return status.sent_at
}

function formatLastUpdated(iso: string): string {
  const { dateTimeFormat } = IFace.getTimeFormats()
  return timeFormat(iso, dateTimeFormat)
}
